import { useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Box, IconButton, Typography } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { toast } from "react-toastify";

const DEFAULT_MAX_FILE_SIZE = 500 * 1024;
const VIDEO_MAX_FILE_SIZE = 5 * 1024 * 1024;
const SLIDE_MULTI_MEDIA_MAX_FILE_SIZE = 10 * 1024 * 1024;

const isVideo = (file, mediaType = "mixed") => {
  if (mediaType === "image") return false;
  if (!file) return false;
  if (typeof file === "string") {
    return /\.mp4$/i.test(file);
  }
  return file.type === "video/mp4" || /\.mp4$/i.test(file.name || "");
};

const FileUploaderMultiMedia = ({
  value = [],
  onChange = () => {},
  accept = "image/*,video/*",
  maxFiles = 5,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  title = "Upload Media",
  helperText = "Drag & drop or click to browse",
  mediaType = "mixed",
}) => {
  const files = Array.isArray(value) ? value : [];
  const maxSizeLabel =
    maxFileSize >= 1024 * 1024
      ? `${Math.round(maxFileSize / (1024 * 1024))} MB`
      : `${Math.round(maxFileSize / 1024)} KB`;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    multiple: true,
    maxFiles,
    maxSize: maxFileSize,
    onDrop: (acceptedFiles) => {
      const remainingSlots = maxFiles - files.length;
      if (remainingSlots <= 0) {
        toast.error(`Maximum ${maxFiles} files allowed`);
        return;
      }
      const filesToAdd = acceptedFiles.slice(0, remainingSlots);
      if (acceptedFiles.length > remainingSlots) {
        toast.warning(`Only ${remainingSlots} more file(s) can be added`);
      }
      onChange([...files, ...filesToAdd]);
    },
    onDropRejected: (rejections) => {
      rejections.forEach(({ file, errors }) => {
        errors.forEach((error) => {
          if (error.code === "file-too-large") {
            toast.error(`${file.name} exceeds ${maxSizeLabel} size limit`);
          } else if (error.code === "file-invalid-type") {
            toast.error(`${file.name} is not an allowed file type`);
          } else {
            toast.error(`${file.name}: ${error.message}`);
          }
        });
      });
    },
  });

  useEffect(() => {
    return () => {
      files.forEach((file) => {
        if (file instanceof File) {
          URL.revokeObjectURL(file);
        }
      });
    };
  }, [files]);

  const MediaIcon = mediaType === "video" ? VideocamOutlinedIcon : ImageOutlinedIcon;

  return (
    <Box>
      <Box
        {...getRootProps()}
        sx={{
          border: "2px dashed",
          borderColor: isDragActive ? "#752ADA" : "#d1d5db",
          borderRadius: 2,
          p: 2.5,
          mb: 2,
          textAlign: "center",
          cursor: "pointer",
          bgcolor: isDragActive ? "rgba(117,42,218,0.06)" : "#fafafa",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: "#752ADA",
            bgcolor: "rgba(117,42,218,0.04)",
          },
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadOutlinedIcon sx={{ fontSize: 36, color: "#752ADA", mb: 0.5 }} />
        <Typography variant="subtitle2" fontWeight={600} color="#1f2937">
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {helperText}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {files.length}/{maxFiles} uploaded · Max {maxSizeLabel} per file
        </Typography>
      </Box>

      {files.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          {files.map((file, index) => {
            const preview =
              typeof file === "string" ? file : URL.createObjectURL(file);

            return (
              <Box
                key={`${preview}-${index}`}
                sx={{
                  position: "relative",
                  width: 120,
                  height: 120,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                }}
              >
                {isVideo(file, mediaType) ? (
                  <video
                    src={preview}
                    controls
                    muted
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <img
                    src={preview}
                    alt="preview"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                )}

                <Box
                  sx={{
                    position: "absolute",
                    bottom: 4,
                    left: 4,
                    bgcolor: "rgba(0,0,0,0.55)",
                    color: "#fff",
                    borderRadius: 1,
                    px: 0.75,
                    py: 0.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.25,
                  }}
                >
                  <MediaIcon sx={{ fontSize: 12 }} />
                  <Typography variant="caption" sx={{ fontSize: 10 }}>
                    {index + 1}
                  </Typography>
                </Box>

                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (file instanceof File) {
                      URL.revokeObjectURL(preview);
                    }
                    onChange(files.filter((_, i) => i !== index));
                  }}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    bgcolor: "#fff",
                    width: 24,
                    height: 24,
                    boxShadow: 1,
                    "&:hover": { bgcolor: "#fee2e2" },
                  }}
                >
                  <Typography sx={{ fontSize: 12, lineHeight: 1 }}>✕</Typography>
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default FileUploaderMultiMedia;
export { DEFAULT_MAX_FILE_SIZE, VIDEO_MAX_FILE_SIZE, SLIDE_MULTI_MEDIA_MAX_FILE_SIZE };
